import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, ButtonGroup } from 'reactstrap';
import TableContainerReactTable from '../../../../../Components/Common/TableContainerReactTable';
import AssociateLocationModal from '../../modals/AssociateLocationModal';
import AssociateVendorModal from '../../modals/vendorModals/AssociateVendorModal';
import LoadingIndicator from '../../../../../Components/Loading/LoadingIndicator';
import { Location } from '../../../../common/location';
import { VendorData } from '../../../../organizations';
import {
  useAssignAllWarehousesToProductMutation,
  useAssignWarehouseToProductMutation,
  useDeleteWarehouseFromProductMutation,
  useGetProductWarehouseQuery,
  useUpdateProductWarehouseMutation,
  useLazyAuditProductWarehouseDetailsQuery
} from '../../../redux/productApiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { ProductWarehouseItem } from '../../../types/productWarehouseTypes';
import { toast } from 'react-toastify';
import { RowSelectionState } from '@tanstack/react-table';
import { useConfirmation } from '../../../../../hooks/useConfirmation';
import DeleteModal from 'Components/Common/DeleteModal';

// Types
interface WarehouseTabProps {
  isActive?: boolean;
}


const WarehouseTab: React.FC<WarehouseTabProps> = ({ isActive = false }) => {

  //Redux store state
  const selectedProductRowId = useSelector((state: RootState) => state.products.selectedProduct?.rowId || 0);

  // Local states
  const [initialUpdatedTimestamps, setInitialUpdatedTimestamps] = useState<{ [rowId: number]: string }>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedRowData, setSelectedRowData] = useState<ProductWarehouseItem | null>(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [isAssigningLocations, setIsAssigningLocations] = useState(false);

  // Track changes for pending changes indicator
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [changedCells, setChangedCells] = useState<{ [key: string]: any }>({});

  // Holds the target warehouse id for vendor modal synchronously
  const vendorTargetWarehouseIdRef = useRef<number | null>(null);

  // Keep a ref to current warehouse data to avoid stale closures
  const warehouseDataRef = useRef<ProductWarehouseItem[]>([]);

  //Redux query for product warehouse
  const {
    data: productWarehouseData,
    isLoading: isLoadingProductWarehouse,
    isFetching: isFetchingProductWarehouse,
    error: productWarehouseError,
    refetch: refetchProductWarehouse
  } = useGetProductWarehouseQuery(
    { productRowId: selectedProductRowId },
    { skip: !isActive || !selectedProductRowId }
  );

  // Mutations 
  const [assignAllWarehousesToProduct] = useAssignAllWarehousesToProductMutation();
  const [getProductWarehouseAuditDetails] = useLazyAuditProductWarehouseDetailsQuery();
  const [deleteWarehouseFromProduct] = useDeleteWarehouseFromProductMutation();
  const [assignWarehouseToProduct] = useAssignWarehouseToProductMutation();
  const [updateProductWarehouse] = useUpdateProductWarehouseMutation();

  // Local warehouse data state - this is our working copy
  const [warehouseData, setWarehouseData] = useState<ProductWarehouseItem[]>([]);

  //Derived states
  const isRefreshing = isFetchingProductWarehouse && !isLoadingProductWarehouse;
  const hasData = warehouseData.length > 0;

  // Confirmation modal hook for deletion of warehouses
  const { confirmation, showConfirmation, hideConfirmation, handleConfirm } = useConfirmation();

  // Define numeric fields for validation
  const numericFields = [
    'waterLevel',
    'reorderQuantity',
    'orderQtyMultiple',
    'defaultAreaId',
    'productLineId',
    'vendorId',
    'averageCost',
  ];

  // Derived state
  const hasUnsavedChanges = Object.keys(changedCells).length > 0;

  // Sync local state with API data
  useEffect(() => {
    if (productWarehouseData?.data) {
      setWarehouseData(productWarehouseData.data);
    } else {
      setWarehouseData([]);
    }
  }, [productWarehouseData]);

  // Update ref whenever warehouseData changes
  useEffect(() => {
    warehouseDataRef.current = warehouseData;
  }, [warehouseData]);

  // Handle timestamp extraction
  useEffect(() => {
    if (productWarehouseData?.data) {
      const timestamps: { [rowId: number]: string } = {};
      productWarehouseData.data.forEach((item: ProductWarehouseItem) => {
        timestamps[item.rowId] = item.updated;
      });
      setInitialUpdatedTimestamps(timestamps);
    }
  }, [productWarehouseData]);

  // Handle API errors
  useEffect(() => {
    if (productWarehouseError) {
      toast.error('Failed to load product warehouse. Please try again.');
    }
  }, [productWarehouseError]);

  // Clear changes when product changes
  useEffect(() => {
    setChangedCells({});
  }, [selectedProductRowId]);

  // Helper function to convert value to appropriate type
  const convertValueToCorrectType = (field: string, value: string) => {
    if (numericFields.includes(field)) {
      const numValue = parseFloat(value);
      return value === '' ? null : isNaN(numValue) ? null : numValue;
    }
    return value;
  };

  const refetchWarehouseDataList = () => {
    setHasPendingChanges(false);
    setRowSelection({});
    setSelectedRowData(null);
    refetchProductWarehouse();
  };

  // Handle assign all warehouses to product
  const handleAssignAllWarehousesToProduct = async () => {
    try {
      await assignAllWarehousesToProduct({ productRowId: selectedProductRowId }).unwrap();
      toast.success('All warehouses assigned to product successfully');
      refetchProductWarehouse();
    } catch (error) {
      toast.error('Failed to assign all warehouses to product');
    }
  };

  // Handle cell changes - directly update warehouse data
  const handleCellChange = useCallback((rowId: number, field: string, newValue: string, originalValue: string) => {
    // Update the warehouse data directly
    setWarehouseData(prev =>
      prev.map(item =>
        item.rowId === rowId
          ? { ...item, [field]: newValue }
          : item
      )
    );

    // Track changes for save functionality
    const cellKey = `${rowId}-${field}`;
    if (newValue === originalValue) {
      // Remove from changes if reverted to original
      setChangedCells(prev => {
        const { [cellKey]: removed, ...rest } = prev;
        return rest;
      });
    } else {
      // Track the change
      setChangedCells(prev => ({
        ...prev,
        [cellKey]: { rowId, field, newValue, originalValue }
      }));
    }
  }, []);

  // Handle save operation
  const handleSave = useCallback(async () => {
    if (!selectedProductRowId || !hasUnsavedChanges || isSaving) return;

    setIsSaving(true);
    try {
      //Group the changes by row - One API call for each row
      const changesByRow: { [rowId: number]: any } = {};
      Object.values(changedCells).forEach((change: any) => {
        if (!changesByRow[change.rowId]) {
          changesByRow[change.rowId] = {};
        }
        changesByRow[change.rowId][change.field] = change.newValue;
      });

      //Check for concurrent modifications before saving


      //Save each row independently
      const saveResults = await Promise.allSettled(
        Object.entries(changesByRow).map(async ([rowIdStr, changes]) => {
          const rowId = parseInt(rowIdStr);
          const originalItem = warehouseData?.find((item: any) => item.rowId === rowId);
          if (!originalItem) return { rowId, success: false, error: 'Original item not found' };

          try {
            //Convert values to correct types before updating
            const convertedChanges: any = {};
            Object.entries(changes).forEach(([field, value]) => {
              convertedChanges[field] = convertValueToCorrectType(field, value as string);
            });


            const auditDetails = await getProductWarehouseAuditDetails({ productRowId: selectedProductRowId, warehouseRowId: rowId }).unwrap();

            // Check for concurrent modifications
            const originalUpdatedTime = originalItem.updated;
            const currentUpdatedTime = auditDetails.data.updated


            if (originalUpdatedTime !== currentUpdatedTime.toString()) {
              return {
                rowId,
                success: false,
                error: `Concurrent modification detected. Record was updated by another user at ${currentUpdatedTime}. Please refresh and try again.`
              };
            }

            const updatedItem = { ...originalItem, ...convertedChanges };


            console.log(updatedItem.activeFlag, updatedItem.excludeEbay, updatedItem.printToSecondPrinter);
            // Create API body with only the required fields
            const apiBody = {
              waterLevel: updatedItem.waterLevel || 0,
              bin: updatedItem.bin || '',
              averageCost: updatedItem.averageCost || 0,
              vendorId: updatedItem.vendorId || 0,
              vendorProductCode: updatedItem.vendorProductCode || '',
              activeFlag: updatedItem.activeFlag === "true" ? true : false,
              reorderQuantity: updatedItem.reorderQuantity || 0,
              orderQtyMultiple: updatedItem.orderQtyMultiple || 0,
              productDesc: updatedItem.productDesc || '',
              excludeEbay: updatedItem.excludeEbay === "true" ? true : false,
              printToSecondPrinter: updatedItem.printToSecondPrinter === "true" ? true : false,
              defaultAreaId: updatedItem.defaultAreaId || 0,
              productLineId: updatedItem.productLineId || 0,
              dynamicField: updatedItem.dynamicField || ''
            };

            await updateProductWarehouse({
              productRowId: selectedProductRowId,
              warehouseRowId: rowId,
              body: apiBody
            }).unwrap();

            return { rowId, success: true };
          } catch (error) {
            return { rowId, success: false, error: error };
          }
        })
      );

      // Process results
      const successfulUpdates = saveResults.filter(result =>
        result.status === 'fulfilled' && result.value.success
      );
      const failedUpdates = saveResults.filter(result =>
        result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)
      );

      // Show appropriate success/error messages
      if (successfulUpdates.length > 0) {
        toast.success(`Successfully updated ${successfulUpdates.length} warehouse item(s)`);
      }

      if (failedUpdates.length > 0) {
        // Check if any failures are due to concurrent modifications
        const concurrentModificationErrors = failedUpdates.filter(result => {
          if (result.status === 'fulfilled') {
            return result.value.error && (result.value.error as string).includes('Concurrent modification detected');
          }
          return false;
        });

        if (concurrentModificationErrors.length > 0) {
          toast.error(`Concurrent modification detected for ${concurrentModificationErrors.length} item(s). Please refresh the data and try again.`);
        } else {
          toast.error(`Failed to update ${failedUpdates.length} warehouse item(s). Please check the data and try again.`);
        }
      }

      // Refresh data to show updated values
      refetchProductWarehouse();

      setChangedCells({});
    } catch (error) {
      toast.error('Failed to save warehouse changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [selectedProductRowId, hasUnsavedChanges, isSaving, changedCells, warehouseData, getProductWarehouseAuditDetails, initialUpdatedTimestamps, updateProductWarehouse, convertValueToCorrectType, refetchProductWarehouse]);

  // Handle cancel - reset to original data
  const handleCancel = useCallback(() => {
    // Reset warehouse data to original API data
    if (productWarehouseData?.data) {
      setWarehouseData(productWarehouseData.data);
    }

    // Clear change tracking
    setChangedCells({});

    toast.info('Changes cancelled');
  }, [productWarehouseData]);

  // Handle checkbox changes (for boolean fields) - directly update warehouse data
  const handleCheckboxChange = useCallback((rowId: number, field: string, value: boolean, originalValue: boolean) => {
    // Update the warehouse data directly
    setWarehouseData(prev =>
      prev.map(item =>
        item.rowId === rowId
          ? { ...item, [field]: value }
          : item
      )
    );

    // Track changes for save functionality
    const cellKey = `${rowId}-${field}`;
    if (value === originalValue) {
      // Remove from changes if reverted to original
      setChangedCells(prev => {
        const { [cellKey]: removed, ...rest } = prev;
        return rest;
      });
    } else {
      // Track the change
      setChangedCells(prev => ({
        ...prev,
        [cellKey]: { rowId, field, newValue: value.toString(), originalValue: originalValue.toString() }
      }));
    }
  }, []);

  const handleNew = () => {
    setIsLocationModalOpen(true);
  };

  // Helper function to get selected row (single selection)
  const getSelectedRow = useCallback(() => {
    const selectedRowIds = Object.keys(rowSelection).filter(key => rowSelection[key]);
    if (selectedRowIds.length === 0) return null;

    const selectedRowId = selectedRowIds[0]; // Only one row can be selected
    return (warehouseData || []).find((row: any) =>
      row.rowId?.toString() === selectedRowId || row.warehouseRowId?.toString() === selectedRowId
    );
  }, [rowSelection, warehouseData]);

  // Show confirmation modal before deleting
  const handleDelete = () => {
    const selectedRow = getSelectedRow();
    if (!selectedRow) {
      toast.error('Please select a warehouse to delete');
      return;
    }

    showConfirmation({
      title: 'Delete Warehouse',
      message: 'This will permanently remove the warehouse from this product. This action cannot be undone.',
      recordName: 'warehouse',
      recordId: selectedRow.shortName || selectedRow.rowId?.toString(),
      deleteButtonText: 'Yes, Delete Warehouse',
      onConfirm: () => performDelete(selectedRow)
    });
  };

  const performDelete = async (selectedRow: any) => {
    if (selectedRow) {
      try {
        await deleteWarehouseFromProduct({ productRowId: selectedProductRowId, warehouseRowId: selectedRow.rowId }).unwrap();
        toast.success('Warehouse deleted successfully');
        refetchWarehouseDataList();
      } catch (error) {
        toast.error('Failed to delete warehouse');
      }
    }
  };

  const handleCloseLocationModal = () => {
    setIsLocationModalOpen(false);
  };

  const handleLocationSelect = async (locations: Location | Location[]) => {
    setIsAssigningLocations(true);

    try {
      if (Array.isArray(locations) && locations.length > 1) {
        // Progress feedback for large batches
        if (locations.length > 3) {
          toast.info(`Assigning ${locations.length} locations...`);
        }

        let successCount = 0;
        const failedLocations: string[] = [];

        // Process locations sequentially
        for (const location of locations) {
          try {
            await assignWarehouseToProduct({
              productRowId: selectedProductRowId,
              locationRowId: location.rowId
            }).unwrap();
            successCount++;
          } catch (error) {
            failedLocations.push(location.name || location.shortName || `Location ${location.rowId}`);
          }
        }

        // Show summary message based on results
        if (successCount === locations.length) {
          toast.success(`All ${locations.length} warehouses assigned successfully`);
        } else if (successCount > 0) {
          toast.warning(`${successCount}/${locations.length} warehouses assigned successfully. Failed: ${failedLocations.join(', ')}`);
        } else {
          toast.error(`Failed to assign any warehouses. Failed locations: ${failedLocations.join(', ')}`);
        }

        // Refetch data to update UI
        refetchWarehouseDataList();
      } else {
        // Single location assignment
        try {
          await assignWarehouseToProduct({
            productRowId: selectedProductRowId,
            locationRowId: (locations as Location[])[0].rowId
          }).unwrap();
          toast.success('Warehouse assigned to product successfully');
          refetchWarehouseDataList();
        } catch (error) {
          toast.error(`Failed to assign warehouse: ${(locations as Location[])[0].name || (locations as Location[])[0].shortName || 'Unknown location'}`);
        }
      }
    } finally {
      setIsAssigningLocations(false);
      setIsLocationModalOpen(false);
    }
  };

  const handleOpenVendorModal = useCallback((warehouseRowId: number) => {
    // Set ref immediately to avoid timing issues with store updates
    vendorTargetWarehouseIdRef.current = warehouseRowId;
    setIsVendorModalOpen(true);
  }, []);

  const handleCloseVendorModal = () => {
    setIsVendorModalOpen(false);
    vendorTargetWarehouseIdRef.current = null;
  };

  const handleVendorSelect = (vendor: VendorData) => {
    if (!vendorTargetWarehouseIdRef.current) {
      handleCloseVendorModal();
      return;
    }

    const targetRowId = vendorTargetWarehouseIdRef.current;

    // Find original values from the base data (use ref to get current data)
    const currentWarehouseData = warehouseDataRef.current;
    const originalRow = currentWarehouseData.find(r => r.rowId === vendorTargetWarehouseIdRef.current);
    if (!originalRow) {
      handleCloseVendorModal();
      return;
    }

    // Update warehouse data directly with vendor information
    setWarehouseData(prev =>
      prev.map(item =>
        item.rowId === targetRowId
          ? { ...item, vendorId: vendor.rowId, vendorName: vendor.name }
          : item
      )
    );

    // Track changes for save functionality
    handleCellChange(vendorTargetWarehouseIdRef.current, 'vendorId', String(vendor.rowId), String(originalRow.vendorId || ''));
    handleCellChange(vendorTargetWarehouseIdRef.current, 'vendorName', vendor.name, originalRow.vendorName || '');

    handleCloseVendorModal();
  };

  const handleRowSelect = (row: any) => {
    const rowData = row.original as ProductWarehouseItem;
    setSelectedRowData(rowData);

    setRowSelection({ [row.id]: true });
  };

  // Check if any row is selected
  const hasSelectedRow = Object.keys(rowSelection).length > 0;

  // Simple table columns - directly use warehouse data values (memoized to prevent re-renders)
  const tableColumns = useMemo(() => {
    return [
      { header: 'Name', accessorKey: 'shortName', size: 180, enableColumnFilter: false },
      {
        header: 'Active',
        accessorKey: 'activeFlag',
        size: 60,
        enableColumnFilter: false,
        cell: (cell: any) => {
          const item = cell.row.original as ProductWarehouseItem;
          const rowId = item.rowId;
          const currentValue = item.activeFlag || false;
          return (
            <div className="text-center">
              <input
                type="checkbox"
                className="form-check-input"
                checked={Boolean(currentValue)}
                onChange={(e) => handleCheckboxChange(rowId, 'activeFlag', e.target.checked, !e.target.checked)}
              />
            </div>
          );
        },
      },
      { header: 'Available', accessorKey: 'available', size: 80, enableColumnFilter: false },
      { header: 'In WH', accessorKey: 'inWH', size: 80, enableColumnFilter: false },
      { header: 'Allocated', accessorKey: 'allocated', size: 80, enableColumnFilter: false },
      { header: 'Pending', accessorKey: 'pending', size: 80, enableColumnFilter: false },
      { header: 'Ready for Dispatch', accessorKey: 'readyForDispatch', size: 120, enableColumnFilter: false },
      { header: 'Back Order', accessorKey: 'backOrder', size: 80, enableColumnFilter: false },
      { header: 'On Order', accessorKey: 'onOrder', size: 80, enableColumnFilter: false },
      {
        header: 'Bin', accessorKey: 'bin', size: 80, enableColumnFilter: false,
        cell: (cell: any) => {
          const item = cell.row.original as ProductWarehouseItem;
          const rowId = item.rowId;
          const currentValue = item.bin || '';

          return (
            <div className="text-center">
              <input
                type="text"
                className="form-control form-control-sm"
                value={currentValue}
                onChange={(e) => handleCellChange(rowId, 'bin', e.target.value, currentValue)}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.currentTarget.select()}
                style={{ textAlign: 'center' }}
              />
            </div>
          );
        },
      },
      { header: 'Average Cost', accessorKey: 'averageCost', size: 100, enableColumnFilter: false },
      { header: 'Capacity', accessorKey: 'capacity', size: 80, enableColumnFilter: false },
      {
        header: 'Order Point', accessorKey: 'waterLevel', size: 100, enableColumnFilter: false,
        cell: (cell: any) => {
          const item = cell.row.original as ProductWarehouseItem;
          const rowId = item.rowId;
          const currentValue = item.waterLevel || '';

          return (
            <div className="text-center">
              <input
                type="text"
                className="form-control form-control-sm"
                value={currentValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (numericFields.includes('waterLevel') && newValue !== '') {
                    const numericRegex = /^-?\d*\.?\d*$/;
                    if (!numericRegex.test(newValue)) return;
                  }
                  handleCellChange(rowId, 'waterLevel', newValue, String(currentValue));
                }}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.currentTarget.select()}
                style={{ textAlign: 'center' }}
              />
            </div>
          );
        },
      },
      {
        header: 'Reorder Quantity', accessorKey: 'reorderQuantity', size: 120, enableColumnFilter: false,
        cell: (cell: any) => {
          const item = cell.row.original as ProductWarehouseItem;
          const rowId = item.rowId;
          const currentValue = item.reorderQuantity || '';

          return (
            <div className="text-center">
              <input
                type="text"
                className="form-control form-control-sm"
                value={currentValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (numericFields.includes('reorderQuantity') && newValue !== '') {
                    const numericRegex = /^-?\d*\.?\d*$/;
                    if (!numericRegex.test(newValue)) return;
                  }
                  handleCellChange(rowId, 'reorderQuantity', newValue, String(currentValue));
                }}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.currentTarget.select()}
                style={{ textAlign: 'center' }}
              />
            </div>
          );
        },
      },
      {
        header: 'Order Qty Multiple', accessorKey: 'orderQtyMultiple', size: 120, enableColumnFilter: false,
        cell: (cell: any) => {
          const item = cell.row.original as ProductWarehouseItem;
          const rowId = item.rowId;
          const currentValue = item.orderQtyMultiple || '';

          return (
            <div className="text-center">
              <input
                type="text"
                className="form-control form-control-sm"
                value={currentValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (numericFields.includes('orderQtyMultiple') && newValue !== '') {
                    const numericRegex = /^-?\d*\.?\d*$/;
                    if (!numericRegex.test(newValue)) return;
                  }
                  handleCellChange(rowId, 'orderQtyMultiple', newValue, String(currentValue));
                }}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.currentTarget.select()}
                style={{ textAlign: 'center' }}
              />
            </div>
          );
        },
      },
      {
        header: 'Vendor',
        accessorKey: 'vendorName',
        size: 180,
        enableColumnFilter: false,
        cell: (cell: any) => {
          const item = cell.row.original as ProductWarehouseItem;
          const rowId = item.rowId;
          const vendorName = item.vendorName || '';

          return (
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ minHeight: '28px' }}
            >
              <span className="text-truncate" style={{ maxWidth: '120px' }} title={vendorName || 'Not assigned'}>
                {vendorName || 'Not assigned'}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenVendorModal(rowId);
                }}
                className="btn btn-outline-primary btn-sm ms-2 px-2 py-1"
                style={{ fontSize: '0.75rem', lineHeight: '1' }}
                title="Select Vendor"
              >
                <i className="ri-edit-line"></i>
              </button>
            </div>
          );
        },
      },
      {
        header: 'Vendor Code', accessorKey: 'vendorProductCode', size: 120, enableColumnFilter: false,
        cell: (cell: any) => {
          const item = cell.row.original as ProductWarehouseItem;
          const rowId = item.rowId;
          const currentValue = item.vendorProductCode || '';

          return (
            <div className="text-center">
              <input
                type="text"
                className="form-control form-control-sm"
                value={currentValue}
                onChange={(e) => handleCellChange(rowId, 'vendorProductCode', e.target.value, currentValue)}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.currentTarget.select()}
                style={{ textAlign: 'center' }}
              />
            </div>
          );
        },
      },
      {
        header: 'Exclude eBay',
        accessorKey: 'excludeEbay',
        size: 100,
        enableColumnFilter: false,
        cell: (cell: any) => {
          const item = cell.row.original as ProductWarehouseItem;
          const rowId = item.rowId;
          const currentValue = item.excludeEbay || false;

          return (
            <div className="text-center">
              <input
                type="checkbox"
                className="form-check-input"
                checked={Boolean(currentValue)}
                onChange={(e) => handleCheckboxChange(rowId, 'excludeEbay', e.target.checked, !e.target.checked)}
              />
            </div>
          );
        },
      },
      {
        header: '2nd Printer',
        accessorKey: 'printToSecondPrinter',
        size: 100,
        enableColumnFilter: false,
        cell: (cell: any) => {
          const item = cell.row.original as ProductWarehouseItem;
          const rowId = item.rowId;
          const currentValue = item.printToSecondPrinter || false;
          return (
            <div className="text-center">
              <input
                type="checkbox"
                className="form-check-input"
                checked={Boolean(currentValue)}
                onChange={(e) => handleCheckboxChange(rowId, 'printToSecondPrinter', e.target.checked, !e.target.checked)}
              />
            </div>
          );
        },
      },
      {
        header: 'Default Area',
        accessorKey: 'defaultAreaName',
        size: 100,
        enableColumnFilter: false,
        cell: (cell: any) => cell.getValue() ? (
          <div className="text-center"><i className="ri-checkbox-circle-line fs-17 align-middle"></i></div>
        ) : null,
      },
      { header: 'Asstd Product Line', accessorKey: 'productLineName', size: 140, enableColumnFilter: false },
      {
        header: 'Dynamic Field',
        accessorKey: 'dynamicField',
        size: 100,
        enableColumnFilter: false,
        cell: (cell: any) => cell.getValue() || 'Not assigned',
      },
    ];
  }, []); // Empty dependency array - columns never change

  return (
    <div className="page-content p-0">
      <Row className="mb-3">
        <Col>
          <ButtonGroup className="gap-2">
            <button onClick={handleNew} className="btn btn-soft-primary btn-sm" disabled={isAssigningLocations}>
              {isAssigningLocations ? (
                <>
                  <span
                    className="spinner-border flex-shrink-0 me-1"
                    role="status"
                    style={{ width: '0.875rem', height: '0.875rem', borderWidth: '1px' }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </span>
                  Assigning...
                </>
              ) : (
                <>
                  <i className="ri-add-line align-middle me-1"></i> New
                </>
              )}
            </button>
            <button 
              onClick={handleSave} 
              className={`btn btn-soft-success btn-sm ${isSaving ? 'btn-load' : ''}`}
              disabled={!hasUnsavedChanges || isSaving}
            >
              {isSaving ? (
                <span className="d-flex align-items-center">
                  <span
                    className="spinner-border flex-shrink-0 me-1"
                    role="status"
                    style={{ width: '0.875rem', height: '0.875rem', borderWidth: '1px' }}
                  >
                    <span className="visually-hidden">Saving...</span>
                  </span>
                  <span>Saving...</span>
                </span>
              ) : (
                <>
                  <i className="ri-save-line align-middle me-1"></i> Save
                </>
              )}
            </button>
            <button onClick={handleCancel} className="btn btn-soft-warning btn-sm" disabled={!hasUnsavedChanges}>
              <i className="ri-close-line align-middle me-1"></i> Cancel
            </button>
            <button onClick={handleDelete} className="btn btn-soft-danger btn-sm" disabled={!hasSelectedRow}>
              <i className="ri-delete-bin-line align-middle me-1"></i> Delete
            </button>
            <button onClick={handleAssignAllWarehousesToProduct} className="btn btn-soft-info btn-sm" disabled={isAssigningLocations}>
              <i className="ri-share-line align-middle me-1"></i> Assign to All
            </button>
            <button
              onClick={refetchWarehouseDataList}
              className={`btn btn-outline-primary btn-sm ${isRefreshing ? 'btn-load' : ''}`}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <span className="d-flex align-items-center">
                  <span
                    className="spinner-border flex-shrink-0"
                    role="status"
                    style={{ width: '0.875rem', height: '0.875rem', borderWidth: '1px' }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </span>
                  <span className="ms-2">
                    Refreshing...
                  </span>
                </span>
              ) : (
                <>
                  <i className="ri-refresh-line align-middle me-1"></i> Refresh
                </>
              )}
            </button>
          </ButtonGroup>
          {hasUnsavedChanges && (
            <div className="text-muted small ms-3 d-flex align-items-center">
              <i className="ri-edit-line me-1"></i>
              Changes pending ({Object.keys(changedCells).length} changes)
            </div>
          )}
        </Col>
      </Row>

      {/* Warehouse Table - Inline implementation */}
      <div className="warehouse-content">
        {hasData ? (
          <TableContainerReactTable
            columns={tableColumns}
            data={warehouseData}
            divClass="table-responsive"
            tableClass="align-middle table-nowrap"
            theadClass="table-light"
            enableRowSelection={true}
            enableMultiRowSelection={false}
            rowSelection={rowSelection}
            onRowSelectionChange={(selection) => setRowSelection(selection as RowSelectionState)}
            onRowClick={handleRowSelect}
            getRowId={(row) => row.rowId.toString()}
            verticalLines={true}
          />
        ) : (
          <div className="text-center py-4">
            <div className="text-muted">
              <i className="ri-database-2-line fs-24 mb-2"></i>
              <p>No warehouse data available for this product.</p>
            </div>
          </div>
        )}
      </div>

      {/* Location Modal */}
      <AssociateLocationModal
        isOpen={isLocationModalOpen}
        onClose={handleCloseLocationModal}
        onSelect={handleLocationSelect}
        title="Pick Location"
        multiSelect={true}
      />

      {/* Vendor Modal */}
      <AssociateVendorModal
        isOpen={isVendorModalOpen}
        onClose={handleCloseVendorModal}
        onSelect={handleVendorSelect}
      />

      {/* Delete Confirmation Modal */}
      {confirmation && (
        <DeleteModal
          show={confirmation.isOpen}
          onDeleteClick={handleConfirm}
          onCloseClick={hideConfirmation}
          title={confirmation.title}
          message={confirmation.message}
          recordName={confirmation.recordName}
          recordId={confirmation.recordId}
          deleteButtonText={confirmation.deleteButtonText}
          isLoading={confirmation.isLoading}
        />
      )}
    </div>
  );
};

export default WarehouseTab; 